import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { calculateProductReadiness, getReadinessStatusBadge } from '../../../services/productValidation';

const ProductCard = ({ product, isSelected, onSelect, onEdit, onDelete, onPreview, onQuickEdit }) => {
  const [showActions, setShowActions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    if (window.confirm(`Tem certeza que deseja excluir "${product?.name}"?`)) {
      setIsDeleting(true);
      await onDelete(product);
      setIsDeleting(false);
    }
  };

  const displayImage = product?.images?.[0] || product?.image || '/assets/images/no_image.png';
  const displayPrice = product?.price || 0;
  const displayStock = product?.stock || 0;

  const getStatusColor = (status) => {
    const colors = {
      ready: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      draft: 'bg-gray-50 text-gray-600 border-gray-200'
    };
    return colors?.[status] || colors?.draft;
  };

  const getChannelIcon = (channel) => {
    const icons = {
      shopee: 'ShoppingBag',
      mercadolivre: 'Store',
      n8n: 'Workflow'
    };
    return icons?.[channel] || 'Package';
  };

  const getPrimaryImage = () => {
    console.log(product);
    if (product?.images && Array.isArray(product?.images) && product?.images?.length > 0) {
      return product?.images?.[0];
    }
    return '/assets/images/no_image.png';
  };

  const handleImageError = (e) => {
    e.currentTarget.src = '/assets/images/no_image.png';
  };
  
  const readiness = calculateProductReadiness(product);
  const shopeeStatus = getReadinessStatusBadge(readiness?.shopee?.ready);
  const mercadolivreStatus = getReadinessStatusBadge(readiness?.mercadolivre?.ready);

  return (
    <>
      <div
        className={`
          bg-white border-2 rounded-xl overflow-hidden transition-all duration-300
          hover:shadow-xl hover:-translate-y-1
          ${isSelected ? 'border-blue-500 shadow-lg ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}
        `}
      >
        <div className="relative">
          <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
            <img
              src={getPrimaryImage()}
              alt={product?.name || 'Product image'}
              onError={handleImageError}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
            />
          </div>

          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {product?.badges?.map((badge, index) => (
                <span
                  key={index}
                  className={`
                    px-3 py-1.5 rounded-full text-xs font-bold shadow-md backdrop-blur-sm
                    ${badge === 'Evergreen' ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : 'bg-white/90 text-gray-800'}
                  `}
                >
                  {badge}
                </span>
              ))}
            </div>

            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e?.stopPropagation();
                onSelect(product?.id);
              }}
              className="w-6 h-6 rounded-md border-2 border-white bg-white/95 cursor-pointer shadow-lg accent-blue-600"
              onClick={(e) => e?.stopPropagation()}
            />
          </div>

          {product?.hasAIContent && (
            <div className="absolute bottom-3 right-3">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg">
                <Icon name="Sparkles" size={14} />
                <span className="text-xs font-bold">IA</span>
              </div>
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h3 className="font-bold text-gray-900 line-clamp-2 flex-1 text-base leading-snug">
              {product?.name}
            </h3>
            <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 whitespace-nowrap ${getStatusColor(product?.status)}`}>
              {product?.status === 'ready' ? 'Pronto' : product?.status === 'pending' ? 'Pendente' : 'Rascunho'}
            </span>
          </div>

          <p className="text-sm text-gray-500 mb-4 font-medium">
            SKU: <span className="text-gray-700">{product?.sku}</span>
          </p>

          <div className="flex items-center justify-between mb-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500 font-semibold mb-1">Custo</p>
              <p className="font-bold text-gray-900 text-lg">
                R$ {product?.cost?.toFixed(2)}
              </p>
            </div>
            <div className="w-px h-10 bg-gray-300"></div>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-semibold mb-1">Preço</p>
              <p className="font-bold text-blue-600 text-lg">
                R$ {product?.price?.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 flex-1">
              <Icon name={getChannelIcon('shopee')} size={16} className="text-orange-600" />
              <span className={`text-xs font-bold ${shopeeStatus?.color}`}>
                {shopeeStatus?.label}
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200 flex-1">
              <Icon name={getChannelIcon('mercadolivre')} size={16} className="text-yellow-600" />
              <span className={`text-xs font-bold ${mercadolivreStatus?.color}`}>
                {mercadolivreStatus?.label}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => onEdit?.(product)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg font-semibold"
            >
              <Icon name="Edit" size={18} />
              <span className="text-sm">Editar</span>
            </button>
            
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center justify-center px-3 py-2 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
              title="Excluir produto"
            >
              <Icon name="Trash2" size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div
            className="fixed inset-0 z-[250] bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="fixed inset-0 z-[260] flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg shadow-brand-xl max-w-md w-full p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={24} className="text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    Confirmar Exclusão
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Tem certeza que deseja excluir <strong>{product?.name}</strong>? Esta ação não pode ser desfeita.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 rounded-md bg-muted text-foreground hover:bg-muted/80 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    onDelete?.(product);
                  }}
                  className="px-4 py-2 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors font-medium"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ProductCard;